(() => {
  // Ultrapure bundle upgrade flow.
  if (customElements.get('bundle-upgrade-modal')) return;

  class BundleUpgradeModal extends HTMLElement {
    constructor() {
      super();
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.bypassNextSubmit = false;
      this.opener = null;
    }

    connectedCallback() {
      this.form = document.getElementById(this.dataset.productFormId);
      this.dialog = this.querySelector('.bundle-upgrade__dialog');
      this.errorMessage = this.querySelector('[data-bundle-upgrade-error]');
      this.continueButton = this.querySelector('[data-bundle-upgrade-continue]');
      this.cancelButtons = this.querySelectorAll('[data-bundle-upgrade-cancel]');
      this.upgradeButtons = this.querySelectorAll('[data-bundle-upgrade-variant]');

      if (!this.form || !this.dialog || !this.continueButton || !this.upgradeButtons.length) return;

      document.addEventListener('submit', this.onFormSubmit, true);
      this.continueButton.addEventListener('click', () => this.continueWithOriginal());
      this.cancelButtons.forEach((button) => button.addEventListener('click', () => this.close()));
      this.upgradeButtons.forEach((button) => {
        button.addEventListener('click', () => this.addUpgrade(button));
      });
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this.onFormSubmit, true);
      document.removeEventListener('keydown', this.onKeydown);
    }

    onFormSubmit(event) {
      if (event.target !== this.form) return;

      if (this.bypassNextSubmit) {
        this.bypassNextSubmit = false;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      this.opener = event.submitter || document.activeElement;
      this.open();
    }

    open() {
      this.resetError();
      this.hidden = false;
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        document.body.classList.add('bundle-upgrade-open');
        document.addEventListener('keydown', this.onKeydown);
        (this.upgradeButtons[0] || this.dialog).focus();
      });
    }

    close({ restoreFocus = true } = {}) {
      this.classList.remove('is-open');
      document.body.classList.remove('bundle-upgrade-open');
      document.removeEventListener('keydown', this.onKeydown);
      this.resetButtons();

      window.setTimeout(() => {
        this.hidden = true;
        if (restoreFocus && this.opener instanceof HTMLElement) this.opener.focus();
      }, 180);
    }

    continueWithOriginal() {
      const submitter = this.opener instanceof HTMLButtonElement ? this.opener : undefined;
      this.close({ restoreFocus: false });
      this.bypassNextSubmit = true;
      window.setTimeout(() => this.form.requestSubmit(submitter), 0);
    }

    async addUpgrade(button) {
      if (button.disabled) return;

      this.resetError();
      this.setLoading(button);

      const formData = new FormData();
      formData.append('id', button.dataset.bundleUpgradeVariant);

      const quantity = Number(this.form.querySelector('[name="quantity"]')?.value || 1);
      formData.append('quantity', Number.isFinite(quantity) && quantity > 0 ? quantity : 1);

      const cart = document.querySelector('cart-drawer');
      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
      }

      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', config);
        const state = await response.json();

        if (!response.ok || state.status) {
          throw new Error(state.description || state.message || 'This bundle could not be added. Please try again.');
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bundle-upgrade',
            productVariantId: button.dataset.bundleUpgradeVariant
          });
        }

        this.close({ restoreFocus: false });

        if (!cart || typeof cart.renderContents !== 'function') {
          window.location = window.routes?.cart_url || '/cart';
          return;
        }

        document.querySelector('.product-sticky-bar')?.classList.remove('is-visible');
        cart.renderContents(state);
        cart.classList.remove('is-empty');
        cart.classList.add('is-visible');
        document.body.classList.add('overflow-hidden');

        if (typeof focusElement !== 'undefined') focusElement = this.opener;
      } catch (error) {
        this.showError(error.message);
        this.resetButtons();
      }
    }

    setLoading(activeButton) {
      this.upgradeButtons.forEach((button) => {
        button.disabled = true;
        button.classList.toggle('is-loading', button === activeButton);
      });
      this.continueButton.disabled = true;
    }

    resetButtons() {
      this.upgradeButtons?.forEach((button) => {
        button.disabled = false;
        button.classList.remove('is-loading');
      });
      if (this.continueButton) this.continueButton.disabled = false;
    }

    resetError() {
      if (!this.errorMessage) return;
      this.errorMessage.hidden = true;
      this.errorMessage.textContent = '';
    }

    showError(message) {
      if (!this.errorMessage) return;
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
      this.errorMessage.focus?.();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        this.dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('bundle-upgrade-modal', BundleUpgradeModal);
})();
